import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Dumbbell, Search,
  Timer, Play, Pause, RotateCcw, CheckCircle2, Clock, TrendingUp,
  Star, BookOpen, History, Copy, Edit2, ChevronDown, ChevronUp, X, Share2
} from "lucide-react";
import { format, addDays, subDays, parseISO } from "date-fns";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import ShareDialog from "@/components/share-dialog";

interface Exercise {
  id: string;
  name: string;
  category: string;
  muscleGroups: string[];
  equipment: string;
  instructions?: string;
  isCustom?: boolean;
}

interface WorkoutSet {
  id: string;
  sessionId: string;
  exerciseName: string;
  exerciseId?: string;
  setNumber: number;
  reps?: number;
  weightLbs?: number;
  durationSeconds?: number;
  distanceKm?: number;
  isWarmup?: boolean;
}

interface WorkoutSession {
  id: string;
  date: string;
  name: string;
  notes?: string;
  durationMinutes?: number;
  isTemplate: boolean;
  sets?: WorkoutSet[];
}

const MUSCLE_GROUPS = ["all", "chest", "back", "shoulders", "biceps", "triceps", "quads", "hamstrings", "glutes", "calves", "core", "full body"];
const CATEGORIES = ["all", "strength", "cardio", "flexibility"];
const EQUIPMENT_COLORS: Record<string, string> = {
  barbell: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  dumbbell: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  machine: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  cable: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  bodyweight: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  none: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400",
  other: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
};

interface WorkoutTemplate {
  name: string;
  description: string;
  category: "strength" | "hypertrophy" | "cardio" | "flexibility" | "full body" | "hiit";
  difficulty: "beginner" | "intermediate" | "advanced";
  durationMin: number;
  goal: string;
  exercises: { name: string; sets: number; reps?: string; notes?: string }[];
  tags: string[];
}

const BUILT_IN_TEMPLATES: WorkoutTemplate[] = [
  // ── PPL Split ──────────────────────────────────────────────────────────────
  {
    name: "Push Day (PPL)", description: "Chest, shoulders, and triceps focused pressing session.",
    category: "hypertrophy", difficulty: "intermediate", durationMin: 60, goal: "Muscle Growth",
    tags: ["PPL", "Chest", "Shoulders", "Triceps"],
    exercises: [
      { name: "Bench Press", sets: 4, reps: "6-8" },
      { name: "Incline Bench Press", sets: 3, reps: "8-10" },
      { name: "Dumbbell Fly", sets: 3, reps: "12-15" },
      { name: "Overhead Press", sets: 4, reps: "6-8" },
      { name: "Lateral Raise", sets: 3, reps: "15-20" },
      { name: "Tricep Pushdown", sets: 3, reps: "12-15" },
      { name: "Skull Crusher", sets: 3, reps: "10-12" },
    ],
  },
  {
    name: "Pull Day (PPL)", description: "Back and biceps focused pulling session.",
    category: "hypertrophy", difficulty: "intermediate", durationMin: 60, goal: "Muscle Growth",
    tags: ["PPL", "Back", "Biceps"],
    exercises: [
      { name: "Pull-Up", sets: 4, reps: "6-8" },
      { name: "Barbell Row", sets: 4, reps: "6-8" },
      { name: "Lat Pulldown", sets: 3, reps: "10-12" },
      { name: "Seated Cable Row", sets: 3, reps: "10-12" },
      { name: "Face Pull", sets: 3, reps: "15-20" },
      { name: "Barbell Curl", sets: 3, reps: "10-12" },
      { name: "Hammer Curl", sets: 3, reps: "12-15" },
    ],
  },
  {
    name: "Leg Day (PPL)", description: "Quad, hamstring, and glute dominant lower body session.",
    category: "hypertrophy", difficulty: "intermediate", durationMin: 65, goal: "Muscle Growth",
    tags: ["PPL", "Legs", "Glutes"],
    exercises: [
      { name: "Squat", sets: 4, reps: "6-8" },
      { name: "Romanian Deadlift", sets: 4, reps: "8-10" },
      { name: "Leg Press", sets: 3, reps: "12-15" },
      { name: "Leg Curl", sets: 3, reps: "12-15" },
      { name: "Leg Extension", sets: 3, reps: "15" },
      { name: "Calf Raise", sets: 4, reps: "15-20" },
      { name: "Lunge", sets: 3, reps: "10 each" },
    ],
  },
  // ── Beginner Programs ─────────────────────────────────────────────────────
  {
    name: "Beginner Full Body A", description: "Simple 3x/week full body routine, alternating with Workout B.",
    category: "strength", difficulty: "beginner", durationMin: 45, goal: "Build Strength",
    tags: ["Beginner", "Full Body", "3x/week"],
    exercises: [
      { name: "Squat", sets: 3, reps: "5" },
      { name: "Bench Press", sets: 3, reps: "5" },
      { name: "Barbell Row", sets: 3, reps: "5" },
      { name: "Plank", sets: 3, reps: "30s", notes: "Hold 30 seconds" },
    ],
  },
  {
    name: "Beginner Full Body B", description: "Alternate with Workout A every other session.",
    category: "strength", difficulty: "beginner", durationMin: 45, goal: "Build Strength",
    tags: ["Beginner", "Full Body", "3x/week"],
    exercises: [
      { name: "Squat", sets: 3, reps: "5" },
      { name: "Overhead Press", sets: 3, reps: "5" },
      { name: "Deadlift", sets: 1, reps: "5" },
      { name: "Plank", sets: 3, reps: "30s", notes: "Hold 30 seconds" },
    ],
  },
  {
    name: "No Equipment Full Body", description: "Bodyweight only — anywhere, no gym required.",
    category: "full body", difficulty: "beginner", durationMin: 30, goal: "General Fitness",
    tags: ["Bodyweight", "No Equipment", "Home"],
    exercises: [
      { name: "Push-Up", sets: 3, reps: "10-15" },
      { name: "Squat", sets: 3, reps: "15-20" },
      { name: "Lunge", sets: 3, reps: "10 each" },
      { name: "Plank", sets: 3, reps: "30s" },
      { name: "Glute Bridge", sets: 3, reps: "15" },
      { name: "Mountain Climber", sets: 3, reps: "20 each" },
      { name: "Tricep Dips", sets: 3, reps: "10-15" },
    ],
  },
  // ── Strength Programs ─────────────────────────────────────────────────────
  {
    name: "5×5 Strength Day A", description: "Classic compound strength program — the gold standard for beginners and intermediates.",
    category: "strength", difficulty: "intermediate", durationMin: 50, goal: "Build Strength",
    tags: ["5x5", "Compound", "Strength"],
    exercises: [
      { name: "Squat", sets: 5, reps: "5" },
      { name: "Bench Press", sets: 5, reps: "5" },
      { name: "Barbell Row", sets: 5, reps: "5" },
    ],
  },
  {
    name: "5×5 Strength Day B", description: "Alternate with Day A every session, add weight each time.",
    category: "strength", difficulty: "intermediate", durationMin: 50, goal: "Build Strength",
    tags: ["5x5", "Compound", "Strength"],
    exercises: [
      { name: "Squat", sets: 5, reps: "5" },
      { name: "Overhead Press", sets: 5, reps: "5" },
      { name: "Deadlift", sets: 1, reps: "5" },
    ],
  },
  {
    name: "Powerlifting Peak", description: "Heavy triples and doubles near competition maxes.",
    category: "strength", difficulty: "advanced", durationMin: 90, goal: "Max Strength",
    tags: ["Powerlifting", "Heavy", "Advanced"],
    exercises: [
      { name: "Squat", sets: 5, reps: "3", notes: "Build to heavy triple" },
      { name: "Bench Press", sets: 5, reps: "3", notes: "Build to heavy triple" },
      { name: "Deadlift", sets: 3, reps: "2", notes: "Heavy doubles" },
      { name: "Pause Squat", sets: 3, reps: "3" },
      { name: "Close-Grip Bench Press", sets: 3, reps: "5" },
    ],
  },
  // ── Body-Part Splits ──────────────────────────────────────────────────────
  {
    name: "Chest & Triceps (Bro Split)", description: "Classic bro split — chest and triceps in one focused session.",
    category: "hypertrophy", difficulty: "intermediate", durationMin: 55, goal: "Muscle Growth",
    tags: ["Bro Split", "Chest", "Triceps"],
    exercises: [
      { name: "Bench Press", sets: 4, reps: "8-10" },
      { name: "Incline Bench Press", sets: 3, reps: "10-12" },
      { name: "Dumbbell Fly", sets: 3, reps: "12-15" },
      { name: "Cable Crossover", sets: 3, reps: "15" },
      { name: "Tricep Pushdown", sets: 4, reps: "12-15" },
      { name: "Skull Crusher", sets: 3, reps: "10-12" },
      { name: "Overhead Tricep Extension", sets: 3, reps: "12" },
    ],
  },
  {
    name: "Back & Biceps (Bro Split)", description: "Wide back and peaked biceps — classic upper body pull session.",
    category: "hypertrophy", difficulty: "intermediate", durationMin: 55, goal: "Muscle Growth",
    tags: ["Bro Split", "Back", "Biceps"],
    exercises: [
      { name: "Pull-Up", sets: 4, reps: "6-10" },
      { name: "Lat Pulldown", sets: 4, reps: "10-12" },
      { name: "Barbell Row", sets: 4, reps: "8-10" },
      { name: "Seated Cable Row", sets: 3, reps: "12" },
      { name: "Barbell Curl", sets: 4, reps: "10-12" },
      { name: "Dumbbell Curl", sets: 3, reps: "12-15" },
      { name: "Hammer Curl", sets: 3, reps: "12" },
    ],
  },
  {
    name: "Shoulders & Arms", description: "Dedicated shoulder and arm day for maximum pump.",
    category: "hypertrophy", difficulty: "intermediate", durationMin: 55, goal: "Muscle Growth",
    tags: ["Shoulders", "Arms", "Pump"],
    exercises: [
      { name: "Overhead Press", sets: 4, reps: "8-10" },
      { name: "Lateral Raise", sets: 4, reps: "15-20" },
      { name: "Front Raise", sets: 3, reps: "12-15" },
      { name: "Rear Delt Fly", sets: 3, reps: "15" },
      { name: "Barbell Curl", sets: 3, reps: "10-12" },
      { name: "Tricep Pushdown", sets: 3, reps: "12-15" },
      { name: "Hammer Curl", sets: 3, reps: "12" },
    ],
  },
  // ── Cardio / HIIT ─────────────────────────────────────────────────────────
  {
    name: "20-Min HIIT Circuit", description: "High-intensity intervals — maximum calorie burn in minimal time.",
    category: "hiit", difficulty: "intermediate", durationMin: 20, goal: "Fat Loss",
    tags: ["HIIT", "Cardio", "Fat Burn", "No Equipment"],
    exercises: [
      { name: "Burpee", sets: 4, reps: "30s on / 15s off" },
      { name: "Mountain Climber", sets: 4, reps: "30s on / 15s off" },
      { name: "Jump Squat", sets: 4, reps: "30s on / 15s off" },
      { name: "High Knees", sets: 4, reps: "30s on / 15s off" },
      { name: "Push-Up", sets: 4, reps: "30s on / 15s off" },
    ],
  },
  {
    name: "Steady State Cardio", description: "Long, moderate-intensity cardio session for endurance and fat loss.",
    category: "cardio", difficulty: "beginner", durationMin: 45, goal: "Endurance",
    tags: ["Cardio", "Endurance", "Low Impact"],
    exercises: [
      { name: "Running", sets: 1, reps: "30 min", notes: "Zone 2 heart rate (~60-70% max HR)" },
      { name: "Cycling", sets: 1, reps: "15 min", notes: "Cool down at easy pace" },
    ],
  },
  // ── Core ──────────────────────────────────────────────────────────────────
  {
    name: "Core Crusher", description: "Comprehensive core workout targeting abs, obliques, and lower back.",
    category: "full body", difficulty: "intermediate", durationMin: 25, goal: "Core Strength",
    tags: ["Core", "Abs", "No Equipment"],
    exercises: [
      { name: "Plank", sets: 3, reps: "60s" },
      { name: "Crunches", sets: 3, reps: "20" },
      { name: "Russian Twist", sets: 3, reps: "20 each" },
      { name: "Leg Raise", sets: 3, reps: "15" },
      { name: "Dead Bug", sets: 3, reps: "10 each" },
      { name: "Side Plank", sets: 3, reps: "45s each" },
      { name: "Ab Wheel Rollout", sets: 3, reps: "10" },
    ],
  },
  // ── Upper / Lower ─────────────────────────────────────────────────────────
  {
    name: "Upper Body Power", description: "Heavy upper body day combining pressing and pulling for strength.",
    category: "strength", difficulty: "intermediate", durationMin: 60, goal: "Build Strength",
    tags: ["Upper Body", "Strength", "Compound"],
    exercises: [
      { name: "Bench Press", sets: 5, reps: "5" },
      { name: "Pull-Up", sets: 5, reps: "5-8" },
      { name: "Overhead Press", sets: 4, reps: "6" },
      { name: "Barbell Row", sets: 4, reps: "6" },
      { name: "Dumbbell Curl", sets: 3, reps: "12" },
      { name: "Tricep Pushdown", sets: 3, reps: "12" },
    ],
  },
  {
    name: "Lower Body Power", description: "Heavy squat and deadlift focused lower body session.",
    category: "strength", difficulty: "intermediate", durationMin: 65, goal: "Build Strength",
    tags: ["Lower Body", "Strength", "Compound"],
    exercises: [
      { name: "Squat", sets: 5, reps: "5" },
      { name: "Deadlift", sets: 3, reps: "5" },
      { name: "Leg Press", sets: 3, reps: "10" },
      { name: "Romanian Deadlift", sets: 3, reps: "10" },
      { name: "Calf Raise", sets: 4, reps: "15" },
    ],
  },
  // ── Flexibility ───────────────────────────────────────────────────────────
  {
    name: "Full Body Stretch", description: "Improve flexibility and reduce soreness — great as a recovery day.",
    category: "flexibility", difficulty: "beginner", durationMin: 30, goal: "Flexibility",
    tags: ["Stretching", "Recovery", "Mobility"],
    exercises: [
      { name: "Hip Flexor Stretch", sets: 2, reps: "60s each", notes: "Hold each side" },
      { name: "Hamstring Stretch", sets: 2, reps: "60s each" },
      { name: "Chest Stretch", sets: 2, reps: "45s each" },
      { name: "Shoulder Stretch", sets: 2, reps: "45s each" },
      { name: "Pigeon Pose", sets: 2, reps: "90s each" },
      { name: "Cat-Cow Stretch", sets: 3, reps: "10 reps" },
      { name: "Thoracic Rotation", sets: 2, reps: "10 each" },
    ],
  },
  // ── Arnold Split ──────────────────────────────────────────────────────────
  {
    name: "Arnold Split — Chest & Back", description: "Arnold's famous 6-day split — Day 1: Chest & Back superset style.",
    category: "hypertrophy", difficulty: "advanced", durationMin: 75, goal: "Muscle Growth",
    tags: ["Arnold Split", "Supersets", "Advanced"],
    exercises: [
      { name: "Bench Press", sets: 4, reps: "8", notes: "Superset with Pull-Up" },
      { name: "Pull-Up", sets: 4, reps: "8" },
      { name: "Incline Bench Press", sets: 4, reps: "10", notes: "Superset with Barbell Row" },
      { name: "Barbell Row", sets: 4, reps: "10" },
      { name: "Dumbbell Fly", sets: 3, reps: "12" },
      { name: "Lat Pulldown", sets: 3, reps: "12" },
      { name: "Cable Crossover", sets: 3, reps: "15" },
    ],
  },
  {
    name: "Arnold Split — Shoulders & Arms", description: "Arnold's 6-day split — Day 2: Delts, biceps, and triceps.",
    category: "hypertrophy", difficulty: "advanced", durationMin: 70, goal: "Muscle Growth",
    tags: ["Arnold Split", "Shoulders", "Arms", "Advanced"],
    exercises: [
      { name: "Overhead Press", sets: 4, reps: "8" },
      { name: "Arnold Press", sets: 3, reps: "10" },
      { name: "Lateral Raise", sets: 4, reps: "15" },
      { name: "Barbell Curl", sets: 4, reps: "10", notes: "Superset with Skull Crusher" },
      { name: "Skull Crusher", sets: 4, reps: "10" },
      { name: "Concentration Curl", sets: 3, reps: "12" },
      { name: "Overhead Tricep Extension", sets: 3, reps: "12" },
    ],
  },
];

const TEMPLATE_CATEGORIES = ["all", "strength", "hypertrophy", "cardio", "hiit", "full body", "flexibility"] as const;
const TEMPLATE_DIFFICULTIES = ["all", "beginner", "intermediate", "advanced"] as const;
const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  intermediate: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  advanced: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};
const CATEGORY_ICONS: Record<string, string> = {
  strength: "💪", hypertrophy: "🏋️", cardio: "🏃", hiit: "⚡", "full body": "🔄", flexibility: "🧘",
};

function RestTimer() {
  const [seconds, setSeconds] = useState(90);
  const [running, setRunning] = useState(false);
  const [initial, setInitial] = useState(90);
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setSeconds(s => {
          if (s <= 1) { setRunning(false); return 0; }
          return s - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const reset = () => { setRunning(false); setSeconds(initial); };
  const pct = (seconds / initial) * 100;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const presetLabel = (s: number) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${s / 60}m`;
    return s % 3600 === 0 ? `${s / 3600}h` : `${Math.floor(s / 3600)}h${Math.round((s % 3600) / 60)}m`;
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Timer className="w-4 h-4 text-primary" />Rest Timer</h3>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {[60, 90, 120, 180, 300, 600, 1800, 3600, 7200].map(s => (
          <button key={s} className={`text-xs px-2 py-1 rounded border transition-colors ${initial === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
            onClick={() => { setInitial(s); setSeconds(s); setRunning(false); }}>
            {presetLabel(s)}
          </button>
        ))}
      </div>
      <div className="relative w-24 h-24 mx-auto mb-3">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 88 88">
          <circle cx="44" cy="44" r="38" fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
          <circle cx="44" cy="44" r="38" fill="none" stroke={seconds === 0 ? "hsl(var(--destructive))" : "hsl(var(--primary))"}
            strokeWidth="6" strokeDasharray={`${2 * Math.PI * 38 * pct / 100} ${2 * Math.PI * 38}`} strokeLinecap="round" className="transition-all" />
        </svg>
        <div className={`absolute inset-0 flex items-center justify-center font-bold ${hours > 0 ? "text-base" : "text-xl"}`}>
          {hours > 0
            ? `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
            : `${mins}:${secs.toString().padStart(2, "0")}`}
        </div>
      </div>
      <div className="flex gap-2 justify-center">
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={reset}><RotateCcw className="w-3.5 h-3.5" /></Button>
        <Button size="sm" className="h-8" onClick={() => setRunning(r => !r)}>
          {running ? <><Pause className="w-3.5 h-3.5 mr-1" />Pause</> : <><Play className="w-3.5 h-3.5 mr-1" />Start</>}
        </Button>
      </div>
    </div>
  );
}

function ElapsedTimer({ startedAt }: { startedAt: number }) {
  const [, forceTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => forceTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);
  const total = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return (
    <span className="font-mono font-semibold text-primary" data-testid="text-workout-elapsed">
      {h > 0 ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}` : `${m}:${s.toString().padStart(2, "0")}`}
    </span>
  );
}

export default function WorkoutPlannerPage() {
  const { toast } = useToast();
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [tab, setTab] = useState("log");
  const [exSearch, setExSearch] = useState("");
  const [exCategory, setExCategory] = useState("all");
  const [exMuscle, setExMuscle] = useState("all");
  const [activeSession, setActiveSession] = useState<WorkoutSession | null>(null);
  const [addExOpen, setAddExOpen] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [newSessionName, setNewSessionName] = useState("");
  const [startOpen, setStartOpen] = useState(false);
  const [customExOpen, setCustomExOpen] = useState(false);
  const [customEx, setCustomEx] = useState({ name: "", category: "strength", equipment: "none", muscleGroups: "" });
  const [pendingTemplate, setPendingTemplate] = useState<WorkoutTemplate | null>(null);
  const [templateCategory, setTemplateCategory] = useState<string>("all");
  const [templateDifficulty, setTemplateDifficulty] = useState<string>("all");
  const [templateSearch, setTemplateSearch] = useState("");
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [sharingSession, setSharingSession] = useState<WorkoutSession | null>(null);

  const { data: exercises = [] } = useQuery<Exercise[]>({
    queryKey: ["/api/health/exercises", exSearch, exCategory, exMuscle],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (exSearch) params.set("q", exSearch);
      if (exCategory !== "all") params.set("category", exCategory);
      if (exMuscle !== "all") params.set("muscle", exMuscle);
      const r = await fetch(`/api/health/exercises?${params}`);
      return r.json();
    },
  });

  const { data: history = [] } = useQuery<WorkoutSession[]>({
    queryKey: ["/api/health/workouts"],
    queryFn: async () => {
      const r = await fetch("/api/health/workouts?isTemplate=false&limit=50");
      return r.json();
    },
  });

  const { data: historyDetail } = useQuery<WorkoutSession>({
    queryKey: ["/api/health/workouts", selectedHistoryId],
    queryFn: async () => {
      const r = await fetch(`/api/health/workouts/${selectedHistoryId}`);
      return r.json();
    },
    enabled: !!selectedHistoryId,
  });

  const { data: todaySessions = [] } = useQuery<WorkoutSession[]>({
    queryKey: ["/api/health/workouts", date],
    queryFn: async () => {
      const r = await fetch("/api/health/workouts?isTemplate=false&limit=10");
      const all: WorkoutSession[] = await r.json();
      return all.filter(s => s.date === date);
    },
  });

  const createSessionMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/health/workouts", data),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/health/workouts"] });
      const session = await data.json();
      setActiveSession({ ...session, sets: [] });
      setStartOpen(false);
      // If started from a template, queue exercises
      if (pendingTemplate) {
        const tmpl = pendingTemplate;
        setPendingTemplate(null);
        // Add one set per exercise in order
        let delay = 0;
        for (const ex of tmpl.exercises) {
          setTimeout(async () => {
            await apiRequest("POST", `/api/health/workouts/${session.id}/sets`, {
              exerciseName: ex.name,
              setNumber: 1,
              reps: parseInt(ex.reps || "10") || 10,
            });
            setActiveSession(s => s ? { ...s, sets: [...(s.sets || []), {
              id: `tmp-${ex.name}`,
              sessionId: session.id,
              exerciseName: ex.name,
              setNumber: 1,
              reps: parseInt(ex.reps || "10") || 10,
            } as WorkoutSet] } : s);
          }, delay);
          delay += 150;
        }
        // Refresh after all done
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ["/api/health/workouts"] }), delay + 500);
        setTab("log");
        toast({ title: `${tmpl.name} loaded! 💪`, description: `${tmpl.exercises.length} exercises added — adjust sets & reps as you go.` });
      }
    },
  });

  const addSetMutation = useMutation({
    mutationFn: ({ sessionId, ...data }: any) => apiRequest("POST", `/api/health/workouts/${sessionId}/sets`, data),
    onSuccess: async (data) => {
      const set = await data.json();
      setActiveSession(s => s ? { ...s, sets: [...(s.sets || []), set] } : s);
    },
    onError: () => {
      toast({ title: "Failed to add set", description: "Please try again.", variant: "destructive" });
    },
  });

  const updateSetMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string; reps?: number | null; weightLbs?: number | null }) =>
      apiRequest("PATCH", `/api/health/sets/${id}`, data),
    onSuccess: async (data) => {
      const updated = await data.json();
      setActiveSession(s => s ? { ...s, sets: (s.sets || []).map(x => x.id === updated.id ? { ...x, ...updated } : x) } : s);
    },
    onError: () => {
      // Bump the row version so the uncontrolled inputs remount and revert to
      // the last persisted values instead of showing the unsaved edit.
      setSetRowVersion(v => v + 1);
      toast({ title: "Failed to save set", description: "Please try again.", variant: "destructive" });
    },
  });

  const deleteSetMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/health/sets/${id}`),
    onSuccess: (_, id) => {
      setActiveSession(s => s ? { ...s, sets: (s.sets || []).filter(x => x.id !== id) } : s);
    },
    onError: () => {
      toast({ title: "Failed to delete set", description: "Please try again.", variant: "destructive" });
    },
  });

  const finishSessionMutation = useMutation({
    mutationFn: ({ id, duration }: { id: string; duration: number }) =>
      apiRequest("PATCH", `/api/health/workouts/${id}`, { durationMinutes: duration }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/health/workouts"] });
      setActiveSession(null);
      setTimerStartedAt(null);
      toast({ title: "Workout saved! 💪" });
    },
    onError: () => {
      toast({ title: "Failed to save workout", description: "Please try again.", variant: "destructive" });
    },
  });

  const createCustomExMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/health/exercises", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/health/exercises"] });
      setCustomExOpen(false);
      toast({ title: "Exercise created" });
    },
  });

  const startTime = useRef<number>(Date.now());
  // When resuming a saved session, previously logged minutes are added to the
  // new elapsed time on finish so the total duration accumulates.
  const priorDuration = useRef<number>(0);
  const [timerStartedAt, setTimerStartedAt] = useState<number | null>(null);
  // Remount key for set weight/reps inputs so persisted values re-sync after
  // a failed save (uncontrolled defaultValue inputs don't track state).
  const [setRowVersion, setSetRowVersion] = useState(0);

  const handleStartWorkout = () => {
    startTime.current = Date.now();
    priorDuration.current = 0;
    setTimerStartedAt(Date.now());
    createSessionMutation.mutate({ date, name: newSessionName || "Workout", isTemplate: false });
  };

  const handleResumeSession = async (sessionId: string) => {
    try {
      const r = await fetch(`/api/health/workouts/${sessionId}`);
      if (!r.ok) throw new Error("load failed");
      const session: WorkoutSession = await r.json();
      startTime.current = Date.now();
      priorDuration.current = session.durationMinutes || 0;
      setTimerStartedAt(Date.now());
      setActiveSession({ ...session, sets: session.sets || [] });
      setTab("log");
    } catch {
      toast({ title: "Couldn't start this workout", description: "Please try again.", variant: "destructive" });
    }
  };

  const handleFinish = () => {
    if (!activeSession) return;
    const durationMins = priorDuration.current + Math.round((Date.now() - startTime.current) / 60000);
    finishSessionMutation.mutate({ id: activeSession.id, duration: durationMins });
  };

  // Group sets by exercise for display
  const groupedSets = (sets: WorkoutSet[]) => {
    const groups: Record<string, WorkoutSet[]> = {};
    for (const s of sets) {
      if (!groups[s.exerciseName]) groups[s.exerciseName] = [];
      groups[s.exerciseName].push(s);
    }
    return groups;
  };

  const [inlineReps, setInlineReps] = useState<Record<string, string>>({});
  const [inlineWeight, setInlineWeight] = useState<Record<string, string>>({});

  const addSet = (exName: string) => {
    if (!activeSession) return;
    const existing = (activeSession.sets || []).filter(s => s.exerciseName === exName);
    const key = exName;
    addSetMutation.mutate({
      sessionId: activeSession.id,
      exerciseName: exName,
      setNumber: existing.length + 1,
      reps: parseInt(inlineReps[key] || "10") || undefined,
      weightLbs: parseFloat(inlineWeight[key] || "0") || undefined,
    });
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-background overflow-hidden" style={{ left: "var(--sidebar-width, 256px)" }}>
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Dumbbell className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold">Workout Planner</h1>
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="log">Log Workout</TabsTrigger>
              <TabsTrigger value="exercises">Exercise Library</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
              <TabsTrigger value="templates">Templates</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {/* LOG WORKOUT TAB */}
        {tab === "log" && (
          <div className="h-full flex overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">
              {/* Date nav */}
              <div className="flex items-center gap-3 mb-5">
                <Button variant="ghost" size="icon" onClick={() => setDate(format(subDays(parseISO(date), 1), "yyyy-MM-dd"))}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" onClick={() => setDate(format(new Date(), "yyyy-MM-dd"))}>
                  {date === format(new Date(), "yyyy-MM-dd") ? "Today" : format(parseISO(date), "MMM d, yyyy")}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDate(format(addDays(parseISO(date), 1), "yyyy-MM-dd"))}><ChevronRight className="w-4 h-4" /></Button>
              </div>

              {!activeSession ? (
                <div>
                  {/* Past sessions for this date */}
                  {todaySessions.length > 0 && (
                    <div className="mb-5 space-y-2">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Today's Sessions</h3>
                      {todaySessions.map(s => (
                        <div key={s.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <div className="font-medium">{s.name}</div>
                            <div className="text-sm text-muted-foreground">{s.durationMinutes ? `${s.durationMinutes} min` : "Completed"}</div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" data-testid={`button-start-session-${s.id}`} onClick={() => handleResumeSession(s.id)}>
                              <Play className="w-3.5 h-3.5 mr-1" />Start
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setSelectedHistoryId(s.id)}>View</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="border-2 border-dashed border-border rounded-xl p-12 text-center">
                    <Dumbbell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Ready to train?</h3>
                    <p className="text-muted-foreground text-sm mb-6">Start a new workout session for {format(parseISO(date), "MMMM d")}</p>
                    <Button size="lg" onClick={() => setStartOpen(true)}>
                      <Play className="w-4 h-4 mr-2" />Start Workout
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-bold">{activeSession.name}</h2>
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />In progress
                        {timerStartedAt && <>· <ElapsedTimer startedAt={timerStartedAt} /></>}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setAddExOpen(true)}>
                        <Plus className="w-4 h-4 mr-1" />Add Exercise
                      </Button>
                      <Button size="sm" onClick={handleFinish} disabled={finishSessionMutation.isPending}>
                        <CheckCircle2 className="w-4 h-4 mr-1" />Finish
                      </Button>
                    </div>
                  </div>

                  {/* Exercise groups */}
                  {Object.entries(groupedSets(activeSession.sets || [])).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No exercises yet. Click "Add Exercise" to start logging sets.
                    </div>
                  )}
                  {Object.entries(groupedSets(activeSession.sets || [])).map(([exName, sets]) => (
                    <div key={exName} className="bg-card border border-border rounded-xl mb-4 overflow-hidden">
                      <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
                        <span className="font-semibold">{exName}</span>
                        <Badge variant="outline" className="text-xs">{sets.length} set{sets.length !== 1 ? "s" : ""}</Badge>
                      </div>
                      <div className="p-3">
                        <div className="grid grid-cols-12 gap-2 text-xs text-muted-foreground mb-2 px-1">
                          <span className="col-span-2">Set</span>
                          <span className="col-span-4">Weight (lbs)</span>
                          <span className="col-span-4">Reps</span>
                          <span className="col-span-2" />
                        </div>
                        {sets.map((s, i) => (
                          <div key={s.id} className="grid grid-cols-12 gap-2 items-center mb-1.5">
                            <span className="col-span-2 text-sm font-medium text-muted-foreground">{s.isWarmup ? "W" : i + 1}</span>
                            <div className="col-span-4">
                              <Input
                                key={`w-${s.id}-${s.weightLbs ?? ""}-${setRowVersion}`}
                                className="h-7 text-sm"
                                type="number"
                                min="0"
                                placeholder="lbs"
                                defaultValue={s.weightLbs ?? ""}
                                disabled={s.id.startsWith("tmp-")}
                                data-testid={`input-set-weight-${s.id}`}
                                onBlur={e => {
                                  const v = e.target.value === "" ? null : parseFloat(e.target.value);
                                  if (v !== (s.weightLbs ?? null) && !(Number.isNaN(v as number))) {
                                    updateSetMutation.mutate({ id: s.id, weightLbs: v });
                                  }
                                }}
                                onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                              />
                            </div>
                            <div className="col-span-4">
                              <Input
                                key={`r-${s.id}-${s.reps ?? ""}-${setRowVersion}`}
                                className="h-7 text-sm"
                                type="number"
                                min="0"
                                placeholder="reps"
                                defaultValue={s.reps ?? ""}
                                disabled={s.id.startsWith("tmp-")}
                                data-testid={`input-set-reps-${s.id}`}
                                onBlur={e => {
                                  const v = e.target.value === "" ? null : parseInt(e.target.value);
                                  if (v !== (s.reps ?? null) && !(Number.isNaN(v as number))) {
                                    updateSetMutation.mutate({ id: s.id, reps: v });
                                  }
                                }}
                                onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                              />
                            </div>
                            <div className="col-span-2 flex justify-end">
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                onClick={() => deleteSetMutation.mutate(s.id)}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {/* Add set row */}
                        <div className="grid grid-cols-12 gap-2 items-center mt-2 pt-2 border-t border-border/50">
                          <span className="col-span-2 text-xs text-muted-foreground">{sets.length + 1}</span>
                          <Input className="col-span-4 h-7 text-sm" placeholder="lbs" type="number" min="0"
                            value={inlineWeight[exName] || ""}
                            onChange={e => setInlineWeight(p => ({ ...p, [exName]: e.target.value }))} />
                          <Input className="col-span-4 h-7 text-sm" placeholder="reps" type="number" min="0"
                            value={inlineReps[exName] || ""}
                            onChange={e => setInlineReps(p => ({ ...p, [exName]: e.target.value }))} />
                          <Button className="col-span-2 h-7 text-xs px-2" size="sm" onClick={() => addSet(exName)}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {Object.keys(groupedSets(activeSession.sets || [])).length > 0 && (
                    <Button variant="outline" className="w-full" onClick={() => setAddExOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />Add Another Exercise
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Right panel: Rest timer */}
            <div className="w-64 flex-shrink-0 border-l border-border p-4 overflow-y-auto space-y-4">
              <RestTimer />
              {activeSession && (
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold mb-2">Session Stats</h3>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Exercises</span>
                      <span className="font-medium">{Object.keys(groupedSets(activeSession.sets || [])).length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Sets</span>
                      <span className="font-medium">{(activeSession.sets || []).length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Volume</span>
                      <span className="font-medium">
                        {Math.round((activeSession.sets || []).reduce((s, x) => s + (x.reps || 0) * (x.weightLbs || 0), 0))} lbs
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* EXERCISE LIBRARY TAB */}
        {tab === "exercises" && (
          <div className="h-full flex flex-col p-6 overflow-hidden">
            <div className="flex gap-3 mb-4 flex-shrink-0">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input className="pl-9" placeholder="Search exercises..." value={exSearch} onChange={e => setExSearch(e.target.value)} />
              </div>
              <Select value={exCategory} onValueChange={setExCategory}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c === "all" ? "All Types" : c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={exMuscle} onValueChange={setExMuscle}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{MUSCLE_GROUPS.map(m => <SelectItem key={m} value={m}>{m === "all" ? "All Muscles" : m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={() => setCustomExOpen(true)}><Plus className="w-4 h-4 mr-1" />Custom</Button>
            </div>
            <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 content-start">
              {exercises.map((ex, i) => (
                <div key={ex.id || i} className="bg-card border border-border rounded-xl p-4 hover:border-primary/50 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-sm">{ex.name}</h3>
                    <Badge variant="outline" className={`text-xs ${EQUIPMENT_COLORS[ex.equipment] || ""}`}>{ex.equipment}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {ex.muscleGroups.map(m => (
                      <span key={m} className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{m}</span>
                    ))}
                  </div>
                  <Badge className="text-xs" variant="secondary">{ex.category}</Badge>
                  {ex.instructions && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{ex.instructions}</p>}
                  {activeSession && (
                    <Button size="sm" className="w-full mt-3 h-7 text-xs" onClick={() => {
                      if (!inlineWeight[ex.name]) setInlineWeight(p => ({ ...p, [ex.name]: "" }));
                      if (!inlineReps[ex.name]) setInlineReps(p => ({ ...p, [ex.name]: "10" }));
                      addSetMutation.mutate({
                        sessionId: activeSession.id, exerciseName: ex.name,
                        setNumber: (activeSession.sets || []).filter(s => s.exerciseName === ex.name).length + 1,
                        reps: 10,
                      });
                      setTab("log");
                    }}>+ Add to Workout</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* HISTORY TAB */}
        {tab === "history" && (
          <div className="h-full flex overflow-hidden">
            <div className="w-72 flex-shrink-0 border-r border-border overflow-y-auto p-4">
              <h3 className="font-semibold mb-3">Past Workouts</h3>
              {history.length === 0 && <p className="text-sm text-muted-foreground">No workouts logged yet.</p>}
              {history.map(s => (
                <button key={s.id} className={`w-full text-left p-3 rounded-lg mb-2 border transition-colors ${selectedHistoryId === s.id ? "bg-primary/10 border-primary" : "bg-card border-border hover:bg-muted/50"}`}
                  onClick={() => setSelectedHistoryId(s.id)}>
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{format(parseISO(s.date), "MMM d, yyyy")} · {s.durationMinutes ? `${s.durationMinutes} min` : "No duration"}</div>
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {!selectedHistoryId ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center"><History className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Select a workout to view details</p></div>
                </div>
              ) : historyDetail ? (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-xl font-bold">{historyDetail.name}</h2>
                    <Button variant="ghost" size="icon" onClick={() => setSharingSession(historyDetail)}>
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{format(parseISO(historyDetail.date), "EEEE, MMMM d, yyyy")} · {historyDetail.durationMinutes ? `${historyDetail.durationMinutes} min` : "Duration not recorded"}</p>
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-card border border-border rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold">{Object.keys(groupedSets(historyDetail.sets || [])).length}</div>
                      <div className="text-xs text-muted-foreground">Exercises</div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold">{(historyDetail.sets || []).length}</div>
                      <div className="text-xs text-muted-foreground">Total Sets</div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold">{Math.round((historyDetail.sets || []).reduce((s, x) => s + (x.reps || 0) * (x.weightLbs || 0), 0)).toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">Volume (lbs)</div>
                    </div>
                  </div>
                  {Object.entries(groupedSets(historyDetail.sets || [])).map(([exName, sets]) => (
                    <div key={exName} className="bg-card border border-border rounded-xl mb-3 overflow-hidden">
                      <div className="px-4 py-2.5 border-b border-border bg-muted/30 font-semibold text-sm">{exName}</div>
                      <div className="p-3 space-y-1">
                        <div className="grid grid-cols-3 text-xs text-muted-foreground mb-1">
                          <span>Set</span><span className="text-center">Weight</span><span className="text-right">Reps</span>
                        </div>
                        {sets.map((s, i) => (
                          <div key={s.id} className="grid grid-cols-3 text-sm">
                            <span className="text-muted-foreground">{s.isWarmup ? "Warmup" : `Set ${i + 1}`}</span>
                            <span className="text-center">{s.weightLbs ? `${s.weightLbs} lbs` : "—"}</span>
                            <span className="text-right">{s.reps ?? "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* TEMPLATES TAB */}
        {tab === "templates" && (
          <div className="h-full flex flex-col overflow-hidden">
            {/* Filters bar */}
            <div className="flex-shrink-0 border-b border-border bg-muted/20 px-6 py-3 flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-40">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Search templates..." value={templateSearch}
                  onChange={e => setTemplateSearch(e.target.value)} />
              </div>
              <div className="flex gap-1 flex-wrap">
                {TEMPLATE_CATEGORIES.map(c => (
                  <button key={c} onClick={() => setTemplateCategory(c)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors capitalize ${templateCategory === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                    {CATEGORY_ICONS[c] && c !== "all" ? `${CATEGORY_ICONS[c]} ` : ""}{c === "all" ? "All Types" : c}
                  </button>
                ))}
              </div>
              <div className="flex gap-1">
                {TEMPLATE_DIFFICULTIES.map(d => (
                  <button key={d} onClick={() => setTemplateDifficulty(d)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors capitalize ${templateDifficulty === d ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                    {d === "all" ? "Any Level" : d}
                  </button>
                ))}
              </div>
            </div>

            {/* Template grid */}
            <div className="flex-1 overflow-y-auto p-6">
              {(() => {
                const filtered = BUILT_IN_TEMPLATES.filter(t => {
                  const matchCat = templateCategory === "all" || t.category === templateCategory;
                  const matchDiff = templateDifficulty === "all" || t.difficulty === templateDifficulty;
                  const q = templateSearch.toLowerCase();
                  const matchSearch = !q || t.name.toLowerCase().includes(q) || t.tags.some(g => g.toLowerCase().includes(q)) || t.goal.toLowerCase().includes(q);
                  return matchCat && matchDiff && matchSearch;
                });
                if (filtered.length === 0) return (
                  <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                    <BookOpen className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm">No templates match your filters.</p>
                  </div>
                );
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map((t, i) => {
                      const isExpanded = expandedTemplate === t.name;
                      return (
                        <div key={i} className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-colors">
                          {/* Card header */}
                          <div className="p-4 pb-3">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-xl">{CATEGORY_ICONS[t.category] || "🏋️"}</span>
                                <h3 className="font-semibold text-sm leading-tight">{t.name}</h3>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${DIFFICULTY_COLORS[t.difficulty]}`}>{t.difficulty}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2.5">{t.description}</p>
                            {/* Meta row */}
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2.5">
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t.durationMin} min</span>
                              <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3" />{t.exercises.length} exercises</span>
                              <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" />{t.goal}</span>
                            </div>
                            {/* Tags */}
                            <div className="flex flex-wrap gap-1 mb-3">
                              {t.tags.slice(0, 3).map(tag => (
                                <span key={tag} className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{tag}</span>
                              ))}
                            </div>
                            {/* Exercise preview / expand */}
                            <button className="w-full text-left text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground transition-colors"
                              onClick={() => setExpandedTemplate(isExpanded ? null : t.name)}>
                              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              {isExpanded ? "Hide exercises" : `Show all ${t.exercises.length} exercises`}
                            </button>
                            {isExpanded && (
                              <div className="mt-2 space-y-1 border-t border-border pt-2">
                                {t.exercises.map((ex, j) => (
                                  <div key={j} className="flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-1.5 text-foreground">
                                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                                      <span>{ex.name}</span>
                                    </div>
                                    <span className="text-muted-foreground">{ex.sets}×{ex.reps || "—"}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Action footer */}
                          <div className="px-4 pb-4">
                            <Button size="sm" className="w-full" onClick={() => {
                              setPendingTemplate(t);
                              setNewSessionName(t.name);
                              setStartOpen(true);
                            }}>
                              <Play className="w-3.5 h-3.5 mr-1.5" />Start This Workout
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Start Workout Dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent >
          <DialogHeader><DialogTitle>Start Workout</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Workout Name</Label>
              <Input className="mt-1" value={newSessionName} onChange={e => setNewSessionName(e.target.value)} placeholder="e.g. Push Day, Leg Day..." />
            </div>
            <div>
              <Label>Date</Label>
              <Input className="mt-1" type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartOpen(false)}>Cancel</Button>
            <Button onClick={handleStartWorkout} disabled={createSessionMutation.isPending}>
              <Play className="w-4 h-4 mr-1" />Start
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Exercise Dialog */}
      <Dialog open={addExOpen} onOpenChange={setAddExOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col">
          <DialogHeader><DialogTitle>Add Exercise</DialogTitle></DialogHeader>
          <Input placeholder="Search exercises..." value={exSearch} onChange={e => setExSearch(e.target.value)} autoFocus className="flex-shrink-0" />
          <div className="flex-1 overflow-y-auto space-y-1 mt-2">
            {exercises.map((ex, i) => (
              <button key={ex.id || i} className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 text-left transition-colors"
                onClick={() => {
                  if (!activeSession) return;
                  const setNum = (activeSession.sets || []).filter(s => s.exerciseName === ex.name).length + 1;
                  addSetMutation.mutate({ sessionId: activeSession.id, exerciseName: ex.name, setNumber: setNum, reps: 10 });
                  setAddExOpen(false);
                }}>
                <div>
                  <div className="font-medium text-sm">{ex.name}</div>
                  <div className="text-xs text-muted-foreground">{ex.muscleGroups.join(", ")}</div>
                </div>
                <Badge variant="outline" className="text-xs">{ex.equipment}</Badge>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Exercise Dialog */}
      <Dialog open={customExOpen} onOpenChange={setCustomExOpen}>
        <DialogContent >
          <DialogHeader><DialogTitle>Create Custom Exercise</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input className="mt-1" value={customEx.name} onChange={e => setCustomEx(p => ({ ...p, name: e.target.value }))} /></div>
            <div>
              <Label>Category</Label>
              <Select value={customEx.category} onValueChange={v => setCustomEx(p => ({ ...p, category: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="strength">Strength</SelectItem>
                  <SelectItem value="cardio">Cardio</SelectItem>
                  <SelectItem value="flexibility">Flexibility</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Equipment</Label>
              <Select value={customEx.equipment} onValueChange={v => setCustomEx(p => ({ ...p, equipment: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["none", "barbell", "dumbbell", "machine", "cable", "bodyweight", "other"].map(e => <SelectItem key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Muscle Groups (comma-separated)</Label><Input className="mt-1" placeholder="e.g. chest, triceps" value={customEx.muscleGroups} onChange={e => setCustomEx(p => ({ ...p, muscleGroups: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomExOpen(false)}>Cancel</Button>
            <Button disabled={!customEx.name || createCustomExMutation.isPending}
              onClick={() => createCustomExMutation.mutate({ ...customEx, muscleGroups: customEx.muscleGroups.split(",").map(s => s.trim()).filter(Boolean) })}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sharingSession && (
        <ShareDialog
          isOpen={!!sharingSession}
          onClose={() => setSharingSession(null)}
          itemId={sharingSession.id}
          itemType="workout_plan"
          itemTitle={sharingSession.name}
        />
      )}
    </div>
  );
}
